import { useState, useEffect, useCallback } from "react";
import CurrencyToggle from "./CurrencyToggle";
import PlansTab from "./PlansTab";
import RolesTab from "./RolesTab";
import CalculatorTab from "./CalculatorTab";
import styles from "./pricing.module.css";
import Navbar from "../../Navbar/Navbar";
import Loop from "../../../../../assets/loader/loop2.svg";
import { errorHandler } from "../../../../../utils";
import { useUserLocation } from "../../../../../hooks/useUserLocation";
import {
  clientSubscriptionPlans,
  clientSubscribeToPlan,
  getClientCurrentSubscription,
} from "../../../../../services/clientCalls";
import SubPopup from "../../../../NewJob/SubPopup/popup";
import { useNavigate, useSearchParams } from "react-router-dom";

const PRICING_TABS = ["plans", "roles", "calc"];

const tabFromSearch = (params) => {
  const tab = params.get("tab");
  return PRICING_TABS.includes(tab) ? tab : "plans";
};

const AwaSourcePricing = ({ onContactSales = () => {} }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => tabFromSearch(searchParams));
  const [currency, setCurrency] = useState("NGN");

  // ── Plan data ────── //
  const [annualPlans, setAnnualPlans] = useState([]);
  const [topUpPlans, setTopUpPlans] = useState([]);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [load, setLoad] = useState(false);

  // ── Subscription modal state ─── //
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscribeIsLoading, setSubscribeIsLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedPlanType, setSelectedPlanType] = useState(null); // "annual" | "topup"
  const [selectedCurrency, setSelectedCurrency] = useState("NGN"); // "NGN" | "USD"
  const navigate = useNavigate();

  const { isNigerian, locationLoading } = useUserLocation();

  // Sync display currency with detected location once resolved
  useEffect(() => {
    if (isNigerian !== null) {
      setCurrency(isNigerian ? "NGN" : "USD");
    }
  }, [isNigerian]);

  useEffect(() => {
    const nextTab = tabFromSearch(searchParams);
    setActiveTab((current) => (current === nextTab ? current : nextTab));
  }, [searchParams]);

  const handleTabChange = (id) => {
    setActiveTab(id);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id === "plans") next.delete("tab");
        else next.set("tab", id);
        return next;
      },
      { replace: true },
    );
  };

  // ── Fetch plans ─── //
  const fetchPlans = async () => {
    try {
      setLoad(true);
      const res = await clientSubscriptionPlans();
      if (res?.data?.data?.data) {
        setAnnualPlans(res?.data?.data?.data?.annualPlans ?? []);
        setTopUpPlans(res?.data?.data?.data?.topUpPlans ?? []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoad(false);
    }
  };

  // ── Fetch active subscription ──── //
  const fetchActiveSubscription = async () => {
    try {
      const res = await getClientCurrentSubscription();
      const sub = res?.data?.data?.data;
      if (sub) setActiveSubscription(sub);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  // ── Open modal ─── //
  const handleSelectPlan = (plan, planType) => {
    // Nigerian users pay NGN, international users pay USD — that's it, no conversion
    const payCurrency = isNigerian ? "NGN" : "USD";
    // const displayPrice = isNigerian ? plan?.priceNGN : plan?.priceUSD;
    setSelectedPlan(plan);
    setSelectedPlanType(planType);
    setSelectedCurrency(payCurrency);
    if (plan?._id && planType && payCurrency) {
      navigate(
        `/checkout?id=${plan?._id}&type=${planType}&cur=${payCurrency}`,
        {
          state: {
            annualPlans,
            topUpPlans,
          },
        },
      );
    }
  };

  // ── Subscribe ─── //
  const handleSubscription = useCallback(async () => {
    if (!selectedPlan) return;
    setSubscribeIsLoading(true);
    try {
      const payload = {
        planId: selectedPlan?._id,
        planType: selectedPlanType,
        currency: selectedCurrency,
      };

      const res = await clientSubscribeToPlan(payload);

      // Paystack authorization_url — check common nesting patterns
      const authUrl =
        res?.data?.data?.authorization_url ??
        res?.data?.data?.data?.authorization_url ??
        res?.data?.data?.data?.data?.authorization_url;

      if (authUrl) {
        window.location.href = authUrl;
      }
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, please check your internet connection and try again.",
            "Error",
          );
    } finally {
      setSubscribeIsLoading(false);
    }
  }, [selectedPlan, selectedPlanType, selectedCurrency]);

  const handleCloseModal = useCallback(() => {
    setShowSubscriptionModal(false);
    setSelectedPlan(null);
    setSelectedPlanType(null);
    setSelectedCurrency("NGN");
  }, []);

  // ── SubPopup shape ────
  // Price is already resolved to the right currency — SubPopup receives it as-is.
  // selectedConvert stays false so SubPopup skips the conversion note.
  const modalPlan = selectedPlan
    ? {
        tier: selectedPlan?.name,
        price:
          selectedCurrency === "NGN"
            ? selectedPlan?.priceNGN
            : selectedPlan?.priceUSD,
        currency: selectedCurrency,
        exchangeRate: 0,
        planLocation: selectedPlan?.planLocation,
        meta:
          selectedPlanType === "annual"
            ? `${selectedPlan?.creditUnits} credits · valid 1 year from purchase`
            : `${selectedPlan?.creditUnit} credits · one-time top-up, never expire`,
      }
    : null;

  // ── Hero stats from live data ──────
  const cheapestTopUp = topUpPlans.reduce(
    (min, p) => (p.priceNGN < (min?.priceNGN ?? Infinity) ? p : min),
    null,
  );
  const cheapestAnnual = annualPlans
    .filter((p) => !p.isCustomPricing)
    .reduce(
      (min, p) => (p.priceNGN < (min?.priceNGN ?? Infinity) ? p : min),
      null,
    );

  const heroStats = [
    {
      label: "Credit Packs",
      sub:
        currency === "NGN"
          ? `from ₦${cheapestTopUp?.priceNGN?.toLocaleString() ?? "70,000"} · no commitment`
          : `from $${cheapestTopUp?.priceUSD?.toLocaleString() ?? "100"} · no commitment`,
    },
    {
      label: "Annual Plans",
      sub:
        currency === "NGN"
          ? `from ₦${cheapestAnnual?.priceNGN?.toLocaleString() ?? "450,000"}/yr · ${cheapestAnnual?.creditUnits ?? 60} credits upfront`
          : `from $${cheapestAnnual?.priceUSD?.toLocaleString() ?? "360"}/yr · ${cheapestAnnual?.creditUnits ?? 60} credits upfront`,
    },
    { label: "30-Day Rematch", sub: "guaranteed on every hire" },
  ];

  const isReady = !load && !locationLoading;

  const tabs = [
    { id: "plans", label: "Pricing" },
    { id: "roles", label: "Role Credits" },
    { id: "calc", label: "Credit Calculator" },
  ];

  return (
    <>
      <Navbar />
      <main className="main-landing-page">
        <div className={styles.page}>
          {/* ── Hero ── always visible */}
          <div className={styles.hero}>
            <div className={styles.eyebrow}>
              <span className={styles.eyebrowDot} />
              AwaSource Hiring Credits
            </div>
            <h1 className={styles.headline}>
              Pricing that moves
              <br />
              with how you hire
            </h1>
            <p className={styles.subheadline}>
              No subscriptions. Buy credits for a one-off hiring round, or
              commit annually for half the price per credit.
            </p>
            {/* {(activeTab === "plans" || activeTab === "calc") && (
              <CurrencyToggle currency={currency} onChange={setCurrency} />
            )} */}
            <div className={styles.heroStatsBar}>
              {heroStats.map((item, i) => (
                <div key={i} className={styles.heroStatItem}>
                  <div className={styles.heroStatLabel}>{item.label}</div>
                  <div className={styles.heroStatSub}>{item.sub}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Loader ── */}
          {!isReady && (
            <div className="flex justify-center items-center my-12">
              <img src={Loop} alt="loader" />
            </div>
          )}

          {/* ── Tab area ── only when ready */}
          {isReady && (
            <>
              <div className={styles.tabNav}>
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`${styles.tabBtn} ${activeTab === tab.id ? styles.tabBtnActive : ""}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className={styles.tabContent}>
                {activeTab === "plans" && (
                  <PlansTab
                    currency={currency}
                    annualPlans={annualPlans}
                    topUpPlans={topUpPlans}
                    activeSubscription={activeSubscription}
                    onBuyPack={(plan) => handleSelectPlan(plan, "topup")}
                    onStartPlan={(plan) => handleSelectPlan(plan, "annual")}
                    onContactSales={onContactSales}
                  />
                )}
                {activeTab === "roles" && <RolesTab />}
                {activeTab === "calc" && <CalculatorTab currency={currency} />}
              </div>
            </>
          )}
        </div>
      </main>

      {showSubscriptionModal && modalPlan && (
        <SubPopup
          order={showSubscriptionModal}
          onClose={() => setShowSubscriptionModal(false)}
          loading={subscribeIsLoading}
          handlePay={handleSubscription}
        />
      )}
    </>
  );
};

export default AwaSourcePricing;
